import { useState } from "react";
import { Sparkles, Loader2, CheckCircle2, Download, Mail } from "lucide-react";
import api, { formatErr } from "../api";
import { trackEvent } from "../lib/analytics";

/**
 * Reusable email-capture widget.
 * Drop <NewsletterCapture source="landing_hero" /> anywhere.
 * Optional variants: 'default' (dark card), 'inline' (compact), 'light' (cream).
 */
export default function NewsletterCapture({
  source = "unknown",
  variant = "default",
  heading = "Get Linda's Top 10 Backyards — free.",
  subheading = "Plus one gardening tip a week from 38 years of experience. No spam, unsubscribe anytime.",
  buttonLabel = "Send me the PDF",
}) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState("idle"); // idle | success | error
  const [downloadUrl, setDownloadUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        source,
      });
      trackEvent("newsletter_signup", {
        source,
        already_subscribed: !!data?.already_subscribed,
      });
      setDownloadUrl(data.download_url || "/api/newsletter/top10-pdf");
      setState("success");
    } catch (err) {
      const msg = formatErr(err);
      setState("error");
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    trackEvent("newsletter_pdf_downloaded", { source });
    // Compose absolute API URL so download works cross-origin
    const base = api.defaults.baseURL;
    window.open(`${base}${downloadUrl}`, "_blank");
  };

  // ---------- Styling variants ----------
  const wrappers = {
    default: "bb-card p-8 md:p-10 text-[#F9F6F0]",
    inline: "bb-card p-6",
    light: "bb-card p-8 md:p-10",
  };
  // Inline styles because .bb-card's background-color overrides Tailwind bg utilities.
  const wrapperStyles = {
    default: { backgroundColor: "#2D4A3E" },
    inline: { backgroundColor: "#F4ECE1" },
    light: { backgroundColor: "#F4ECE1" },
  };
  const isDark = variant === "default";

  if (state === "success") {
    return (
      <div className={`${wrappers[variant]} text-center`} style={wrapperStyles[variant]} data-testid={`newsletter-success-${source}`}>
        <div className={`w-14 h-14 rounded-full ${isDark ? "bg-[#D47C59]" : "bg-[#2D4A3E]"} flex items-center justify-center mx-auto mb-4`}>
          <CheckCircle2 className={`w-7 h-7 ${isDark ? "text-[#1A2F25]" : "text-[#F9F6F0]"}`} strokeWidth={2} />
        </div>
        <p className="overline text-[#D47C59] mb-2">You&apos;re on the list! 🌷</p>
        <h3 className={`font-serif text-3xl md:text-4xl mb-3 leading-tight ${isDark ? "text-[#F9F6F0]" : "text-[#1A2F25]"}`}>
          Your PDF is ready.
        </h3>
        <p className={`text-sm md:text-base mb-6 font-light max-w-md mx-auto leading-relaxed ${isDark ? "text-[#E5E0D8]" : "text-[#5C6B62]"}`}>
          Click below to download <em>Linda&apos;s Top 10 Backyards</em> — a 12-page look at her favorite designs, with notes on each.
        </p>
        <button
          onClick={downloadPdf}
          className={isDark ? "btn-accent" : "btn-primary"}
          data-testid={`newsletter-download-${source}`}
        >
          <Download className="w-4 h-4" /> Download the PDF
        </button>
      </div>
    );
  }

  return (
    <div className={wrappers[variant]} style={wrapperStyles[variant]} data-testid={`newsletter-form-${source}`}>
      <div className="flex items-start gap-4 mb-5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? "bg-[#D47C59] text-[#1A2F25]" : "bg-[#2D4A3E] text-[#F9F6F0]"}`}>
          <Sparkles className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <p className={`overline mb-1 ${isDark ? "text-[#D47C59]" : "text-[#D47C59]"}`}>Free download</p>
          <h3 className={`font-serif text-2xl md:text-3xl font-light leading-tight ${isDark ? "text-[#F9F6F0]" : "text-[#1A2F25]"}`}>
            {heading}
          </h3>
          <p className={`text-sm font-light mt-2 leading-relaxed ${isDark ? "text-[#E5E0D8]" : "text-[#5C6B62]"}`}>
            {subheading}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Mail className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? "text-[#5C6B62]" : "text-[#5C6B62]"}`} />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@garden.com"
            className={`bb-input !pl-10 ${isDark ? "bg-[#F9F6F0] text-[#1A2F25]" : ""}`}
            data-testid={`newsletter-email-${source}`}
            aria-label="Your email"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className={isDark ? "btn-accent" : "btn-primary"}
          data-testid={`newsletter-submit-${source}`}
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : buttonLabel}
        </button>
      </form>

      <p className={`text-xs font-light mt-3 ${isDark ? "text-[#E5E0D8]/70" : "text-[#5C6B62]"}`}>
        We&apos;ll never share your email. One tip a week, that&apos;s it.
      </p>
    </div>
  );
}
